import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './PasswordReset.css';

const PasswordReset = () => {
  const [requestEmail, setRequestEmail] = useState('');
  const [requestStatus, setRequestStatus] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const presetToken = params.get('token');
    const presetEmail = params.get('email');
    if (presetToken) {
      setToken(presetToken);
    }
    if (presetEmail) {
      setRequestEmail(presetEmail);
    }
  }, []);

  const handleResetRequest = async (event) => {
    event.preventDefault();
    if (!requestEmail) {
      setRequestStatus({ type: 'error', message: 'メールアドレスを入力してください。' });
      return;
    }

    setIsRequesting(true);
    setRequestStatus(null);
    try {
      const response = await fetch('/api/admin/password-reset/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: requestEmail }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || '確認メールの送信に失敗しました。');
      }

      setRequestStatus({
        type: 'success',
        message: '確認メールを送信しました。受信箱をご確認ください。',
      });
    } catch (error) {
      setRequestStatus({ type: 'error', message: error.message });
    } finally {
      setIsRequesting(false);
    }
  };

  const handleConfirmReset = async (event) => {
    event.preventDefault();
    if (!token || !newPassword) {
      setConfirmStatus({ type: 'error', message: 'トークンと新しいパスワードを入力してください。' });
      return;
    }

    setIsConfirming(true);
    setConfirmStatus(null);
    try {
      const response = await fetch('/api/admin/password-reset/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password: newPassword }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || 'パスワードの再設定に失敗しました。');
      }

      setConfirmStatus({
        type: 'success',
        message: 'パスワードを更新しました。新しいパスワードでログインしてください。',
      });
      setNewPassword('');
    } catch (error) {
      setConfirmStatus({ type: 'error', message: error.message });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="password-reset-page">
      <Header />
      <main className="password-reset-content">
        <div className="password-reset-card">
          <h1>管理者パスワードの再設定</h1>
          <p className="password-reset-intro">
            管理者アカウントのパスワードを忘れた場合は、登録済みメールアドレスに確認メールを送信し、
            リンクから新しいパスワードを登録してください。
          </p>

          <section className="password-reset-section">
            <h2>1. 確認メールの送信</h2>
            <p>
              登録済みのメールアドレスを入力し、「確認メールを送信」を押してください。
              入力されたアドレス宛にトークン付きのURLを送ります。
            </p>
            <form onSubmit={handleResetRequest} className="password-reset-form">
              <label htmlFor="reset-email">
                メールアドレス
                <input
                  id="reset-email"
                  type="email"
                  value={requestEmail}
                  onChange={(event) => setRequestEmail(event.target.value)}
                  placeholder="kei@sugano.works"
                  autoComplete="email"
                />
              </label>
              {requestStatus && (
                <p className={`password-reset-status ${requestStatus.type}`}>
                  {requestStatus.message}
                </p>
              )}
              <button type="submit" disabled={isRequesting}>
                {isRequesting ? '送信中...' : '確認メールを送信'}
              </button>
            </form>
          </section>

          <section className="password-reset-section">
            <h2>2. 新しいパスワードを登録</h2>
            <p>
              メールに記載されたトークンと新しいパスワードを入力してください。
              メール内のリンクからアクセスした場合は自動でトークンがセットされます。
            </p>
            <form onSubmit={handleConfirmReset} className="password-reset-form">
              <label htmlFor="reset-token">
                トークン
                <input
                  id="reset-token"
                  type="text"
                  value={token}
                  onChange={(event) => setToken(event.target.value)}
                  placeholder="例: 2f9a..."
                />
              </label>
              <label htmlFor="reset-password">
                新しいパスワード
                <input
                  id="reset-password"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="新しいパスワード"
                  autoComplete="new-password"
                />
              </label>
              {confirmStatus && (
                <p className={`password-reset-status ${confirmStatus.type}`}>
                  {confirmStatus.message}
                </p>
              )}
              <button type="submit" disabled={isConfirming}>
                {isConfirming ? '更新中...' : 'パスワードを更新'}
              </button>
            </form>
          </section>

          <div className="password-reset-footer">
            <p>
              <Link to="/admin">管理画面へ戻る</Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PasswordReset;
